import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Rocket, Upload, X, RefreshCw } from "lucide-react";

const EMPTY = { segment: "student", name: "", daily_budget_inr: 500, age_min: 18, age_max: 30, headline: "", message: "", image_hash: "", form_id: "" };
const errText = (err) => (typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong");

const CityPicker = ({ cities, setCities }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const search = async () => {
    if (q.trim().length < 2) return;
    try {
      const { data } = await api.get("/admin/meta/cities", { params: { q } });
      setResults(data);
    } catch (err) {
      toast.error(errText(err));
    }
  };
  return (
    <div>
      <Label>Cities</Label>
      <div className="mt-1 flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())} placeholder="Search e.g. Ahmedabad" data-testid="meta-city-search" />
        <Button type="button" variant="outline" onClick={search} data-testid="meta-city-search-btn">Search</Button>
      </div>
      {results.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {results.map((c) => (
            <button type="button" key={c.key} onClick={() => { if (!cities.some((x) => x.key === c.key)) setCities([...cities, c]); setResults([]); setQ(""); }}
              className="rounded-full border border-slate-200 px-2.5 py-1 text-xs hover:bg-amber-50" data-testid="meta-city-result">{c.name}, {c.region}</button>
          ))}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {cities.map((c) => (
          <Badge key={c.key} className="gap-1 bg-amber-100 text-amber-800" data-testid="meta-city-chip">{c.name}<X className="h-3 w-3 cursor-pointer" onClick={() => setCities(cities.filter((x) => x.key !== c.key))} /></Badge>
        ))}
      </div>
    </div>
  );
};

export const MetaAdsLauncher = () => {
  const [f, setF] = useState(EMPTY);
  const [cities, setCities] = useState([]);
  const [forms, setForms] = useState([]);
  const [ads, setAds] = useState([]);
  const [busy, setBusy] = useState("");
  const loadAds = () => api.get("/admin/meta/ads").then(({ data }) => setAds(data)).catch(() => {});
  useEffect(() => {
    loadAds();
    api.get("/admin/meta/forms").then(({ data }) => setForms(data)).catch(() => {});
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("image");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/admin/meta/image", fd);
      setF((x) => ({ ...x, image_hash: data.image_hash }));
      toast.success("Image uploaded to Meta");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };
  const launch = async (e) => {
    e.preventDefault();
    setBusy("launch");
    try {
      await api.post("/admin/meta/launch", { ...f, daily_budget_inr: +f.daily_budget_inr, age_min: +f.age_min, age_max: +f.age_max, city_keys: cities.map((c) => c.key) });
      toast.success("Ad created in Meta (paused). Review and switch it on in Ads Manager.");
      setF(EMPTY);
      setCities([]);
      loadAds();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };
  const syncLeads = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/admin/meta/sync-leads");
      toast.success(`${data.added} new instant-form lead(s) imported`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="mt-4 space-y-5" data-testid="meta-launcher">
      <form onSubmit={launch} className="grid gap-4 sm:grid-cols-2">
        <div><Label>Funnel</Label>
          <Select value={f.segment} onValueChange={(v) => setF({ ...f, segment: v })}>
            <SelectTrigger className="mt-1" data-testid="meta-segment"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="student">Students</SelectItem><SelectItem value="employer">Employers</SelectItem><SelectItem value="expert">Experts</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Ad name</Label><Input required minLength={3} value={f.name} onChange={set("name")} className="mt-1" placeholder="June freshers Ahmedabad" data-testid="meta-name" /></div>
        <div><Label>Daily budget (₹)</Label><Input type="number" min={100} required value={f.daily_budget_inr} onChange={set("daily_budget_inr")} className="mt-1" data-testid="meta-budget" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Age from</Label><Input type="number" min={18} max={65} value={f.age_min} onChange={set("age_min")} className="mt-1" data-testid="meta-age-min" /></div>
          <div><Label>Age to</Label><Input type="number" min={18} max={65} value={f.age_max} onChange={set("age_max")} className="mt-1" data-testid="meta-age-max" /></div>
        </div>
        <div className="sm:col-span-2"><CityPicker cities={cities} setCities={setCities} /></div>
        <div><Label>Headline</Label><Input required maxLength={60} value={f.headline} onChange={set("headline")} className="mt-1" data-testid="meta-headline" /></div>
        <div><Label>Where should people go?</Label>
          <Select value={f.form_id || "funnel"} onValueChange={(v) => setF({ ...f, form_id: v === "funnel" ? "" : v })}>
            <SelectTrigger className="mt-1" data-testid="meta-destination"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="funnel">My funnel page (tracked link)</SelectItem>
              {forms.map((fm) => <SelectItem key={fm.id} value={fm.id}>Instant form: {fm.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2"><Label>Ad text</Label><Textarea required minLength={10} maxLength={500} rows={3} value={f.message} onChange={set("message")} className="mt-1" data-testid="meta-message" /></div>
        <div className="flex items-center gap-3 sm:col-span-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm hover:border-amber-400" data-testid="meta-image-upload">
            {busy === "image" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {f.image_hash ? "Image ready ✓ (change)" : "Upload ad image (JPG/PNG)"}
            <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={upload} data-testid="meta-image-input" />
          </label>
          <Button type="submit" disabled={!!busy || !f.image_hash || !cities.length} className="ml-auto bg-amber-600 hover:bg-amber-700" data-testid="meta-launch">
            {busy === "launch" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Rocket className="mr-1.5 h-4 w-4" />} Create paused ad
          </Button>
        </div>
      </form>
      <div>
        <div className="flex items-center justify-between">
          <p className="font-bold text-slate-900">Ads created from here</p>
          <Button size="sm" variant="outline" onClick={syncLeads} disabled={!!busy} data-testid="meta-sync-leads"><RefreshCw className="mr-1.5 h-4 w-4" /> Import instant-form leads</Button>
        </div>
        {ads.length === 0 ? <p className="mt-2 text-sm text-slate-500">No ads yet.</p> : (
          <div className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="meta-ads-list">
            {ads.map((a) => (
              <div key={a.meta_ad_id} className="flex flex-wrap items-center gap-2 p-3 text-sm" data-testid="meta-ad-row">
                <span className="font-semibold">{a.name}</span>
                <Badge variant="outline">{a.segment}</Badge>
                <Badge className="bg-slate-100 text-slate-700">{a.status}</Badge>
                <span className="text-slate-500">₹{a.daily_budget_inr}/day · source: {a.channel}{a.form_id ? " · instant form" : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
